/**
 * `x-device-id` request header for the SMS transport (analog of AIM's
 * `deviceIdHeader.ts`).
 *
 * The backend binds a session's device identity to the credential: the
 * `x-device-id` on the LOGIN request is minted into a signed `device_id`
 * claim on the access token, carried forward (never re-read) on refresh, and
 * copied off the verified token when a WebSocket ticket is issued
 * (`server/api/v2/ws_ticket.py`). A socket's own claim of identity is
 * deliberately ignored, so login is the one request where web gets to say
 * which device it is. SMS's login surfaces are NOT `client.ky` — they are
 * the raw-`fetch` SimpleJWT adapter (`/rest/sessions`, SSO bootstrap,
 * logout; `@aegix/auth-identity`) and the `rest/login-location/` SSO probe
 * in `pages/auth/Login.tsx` (which forwards the value into SAML RelayState /
 * OIDC state) — hence `deviceIdFetch` below alongside the ky hook.
 *
 * Scope: the raw surfaces ONLY, and that is deliberate — the typed
 * openapi-fetch surface must NOT carry this header. SMS's `deleteDevice`
 * (`client.DELETE('/api/v2/fcm-token/{id}/')`, `services/users.service.ts`)
 * is the same admin sweep AIM documents in its `deviceIdHeader.ts`:
 * `FCMDeviceTokenViewSet` deactivates *every* active token whose
 * `device_id` matches the header, so stamping the typed surface would turn
 * "remove this user's stale token" into "…and silently deregister my own
 * browser from push". Nothing on the typed surface needs the header.
 */
import type { BeforeRequestState } from 'ky';
import { getPersistedSmsDeviceId } from '../push/deviceId';

export const DEVICE_ID_HEADER = 'x-device-id';

/**
 * ky `beforeRequest` hook (ky v2: hooks receive a single state
 * object) for the `client.ky` escape hatch. Adds the persisted device id,
 * or nothing at all when this browser has none yet.
 */
export function deviceIdBeforeRequest({ request }: BeforeRequestState): void {
  // An explicit per-call header wins: the FCM register/deregister calls
  // (`@aegix/web-push` fcm-api) name the device they act on, which is not
  // necessarily the one this browser has persisted.
  if (request.headers.has(DEVICE_ID_HEADER)) return;

  const deviceId = getPersistedSmsDeviceId();
  if (deviceId) {
    request.headers.set(DEVICE_ID_HEADER, deviceId);
  }
}

/**
 * `fetch`-compatible wrapper for SMS's raw-fetch surfaces that sit outside
 * `client.ky`: the SimpleJWT login adapter (`fetchImpl` config point) and
 * the login-page SSO probe. Merges the persisted device id into the request
 * headers; an explicit per-call header wins, and a browser with no
 * persisted id sends the request untouched (read-only — the transport
 * never mints an identity).
 */
export const deviceIdFetch: typeof fetch = (input, init) => {
  const deviceId = getPersistedSmsDeviceId();
  if (!deviceId) return fetch(input, init);

  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  if (!headers.has(DEVICE_ID_HEADER)) {
    headers.set(DEVICE_ID_HEADER, deviceId);
  }
  return fetch(input, { ...init, headers });
};
